export type InlineStyle = 'bold' | 'italic' | 'strikethrough' | 'monospace' | 'spoiler';

export interface InlineTextSegment {
  text: string;
  styles: InlineStyle[];
}

type DelimiterStyle =
  'bold-asterisk' |
  'bold-underscore' |
  'italic-asterisk' |
  'italic-underscore' |
  'strikethrough' |
  'monospace' |
  'spoiler';

interface DelimiterToken {
  start: number;
  end: number;
  style: DelimiterStyle;
  pair?: DelimiterToken;
  opening?: boolean;
}

export function formatInlineText(text: string | null | undefined): InlineTextSegment[] {
  if (!text)
    return text === '' ? [] : [{ text: text || '', styles: [] }];

  const tokens = findDelimiterTokens(text);
  const activeStyles: InlineStyle[] = [];
  const segments: InlineTextSegment[] = [];
  let tokenIndex = 0;
  let position = 0;

  while (position < text.length) {
    const token = tokenIndex < tokens.length ? tokens[tokenIndex] : undefined;
    if (!token || token.start !== position) {
      appendSegment(segments, text.charAt(position), activeStyles);
      position++;
      continue;
    }

    tokenIndex++;
    if (!token.pair) {
      appendSegment(segments, text.substring(token.start, token.end), activeStyles);
    } else if (token.opening) {
      activeStyles.push(displayStyle(token.style));
    } else {
      activeStyles.pop();
    }
    position = token.end;
  }

  return segments;
}

function appendSegment(segments: InlineTextSegment[], text: string, styles: InlineStyle[]) {
  const distinctStyles = styles.filter((style, index) => styles.indexOf(style) === index);
  const previous = segments.length > 0 ? segments[segments.length - 1] : undefined;
  if (previous && sameStyles(previous.styles, distinctStyles)) {
    previous.text += text;
  } else {
    segments.push({ text, styles: distinctStyles.slice() });
  }
}

function sameStyles(left: InlineStyle[], right: InlineStyle[]): boolean {
  return left.length === right.length && left.every((style, index) => style === right[index]);
}

function displayStyle(style: DelimiterStyle): InlineStyle {
  if (style === 'bold-asterisk' || style === 'bold-underscore')
    return 'bold';
  if (style === 'italic-asterisk' || style === 'italic-underscore')
    return 'italic';
  return style;
}

function findDelimiterTokens(text: string): DelimiterToken[] {
  const tokens: DelimiterToken[] = [];
  const openings: DelimiterToken[] = [];

  for (let position = 0; position < text.length;) {
    if (isEscaped(text, position)) {
      position++;
      continue;
    }

    const character = text.charAt(position);
    if (character === '*' || character === '_') {
      const runLength = delimiterRunLength(text, position, character);
      if (runLength <= 3) {
        processEmphasisRun(text, position, runLength, character, openings, tokens);
      }
      position += runLength;
      continue;
    }

    let style: DelimiterStyle | undefined;
    let delimiterLength = 0;
    if (character === '~' || character === '|' || character === '`') {
      const runLength = delimiterRunLength(text, position, character);
      const expectedLength = character === '`' ? 1 : 2;
      if (runLength === expectedLength) {
        style = character === '~' ? 'strikethrough' : character === '|' ? 'spoiler' : 'monospace';
        delimiterLength = expectedLength;
      } else {
        position += runLength;
        continue;
      }
    }

    if (!style) {
      position++;
    } else {
      processToken(text, { start: position, end: position + delimiterLength, style }, openings, tokens);
      position += delimiterLength;
    }
  }

  return tokens;
}

function processEmphasisRun(
  text: string,
  position: number,
  runLength: number,
  delimiter: string,
  openings: DelimiterToken[],
  tokens: DelimiterToken[]
) {
  let remaining = runLength;
  let tokenPosition = position;
  const bold: DelimiterStyle = delimiter === '*' ? 'bold-asterisk' : 'bold-underscore';
  const italic: DelimiterStyle = delimiter === '*' ? 'italic-asterisk' : 'italic-underscore';

  while (remaining > 0) {
    const topStyle = openings.length > 0 ? openings[openings.length - 1].style : undefined;
    const closeNestedItalic = remaining >= 3 && topStyle === italic;
    const delimiterLength = closeNestedItalic || remaining < 2 ? 1 : 2;
    const style = delimiterLength === 1 ? italic : bold;

    processToken(text, { start: tokenPosition, end: tokenPosition + delimiterLength, style }, openings, tokens);
    tokenPosition += delimiterLength;
    remaining -= delimiterLength;
  }
}

function processToken(
  text: string,
  token: DelimiterToken,
  openings: DelimiterToken[],
  tokens: DelimiterToken[]
) {
  const opening = openings.length > 0 ? openings[openings.length - 1] : undefined;
  if (canClose(text, token) && opening && opening.style === token.style) {
    openings.pop();
    opening.pair = token;
    token.pair = opening;
    token.opening = false;
    tokens.push(token);
  } else if (canOpen(text, token)) {
    token.opening = true;
    openings.push(token);
    tokens.push(token);
  }
}

function canOpen(text: string, token: DelimiterToken): boolean {
  if (token.end >= text.length || isWhitespace(text.charAt(token.end)))
    return false;
  return token.start === 0 || !isLetterOrDigit(text.charAt(token.start - 1));
}

function canClose(text: string, token: DelimiterToken): boolean {
  if (token.start === 0 || isWhitespace(text.charAt(token.start - 1)))
    return false;
  return token.end >= text.length || !isLetterOrDigit(text.charAt(token.end));
}

function delimiterRunLength(text: string, position: number, delimiter: string): number {
  let end = position;
  while (end < text.length && text.charAt(end) === delimiter)
    end++;
  return end - position;
}

function isEscaped(text: string, position: number): boolean {
  let backslashes = 0;
  for (let index = position - 1; index >= 0 && text.charAt(index) === '\\'; index--)
    backslashes++;
  return backslashes % 2 === 1;
}

function isWhitespace(character: string): boolean {
  return /\s/.test(character);
}

function isLetterOrDigit(character: string): boolean {
  return /[\p{L}\p{N}]/u.test(character);
}
